import { createValidationError } from "./errors.js";
import { hasValue } from "./shared.js";

const toLengthDetails = ({ min, max } = {}) => {
    const details = {};
    if (Number.isFinite(min)) { details.min = min; }
    if (Number.isFinite(max)) { details.max = max; }
    return details;
};

const toTextLength = value => String(value ?? "").length;

const testRegex = (regex, value) => {
    regex.lastIndex = 0;
    return regex.test(String(value));
};

export const runValidators = (value, validators) => {
    for (const validate of validators) {
        const error = validate(value);
        if (error) { return error; }
    }
};

export const validateRequired = value => {
    if (hasValue(value)) { return; }
    return createValidationError("required");
};

export const validateRequiredTrue = value => {
    if (value === true) { return; }
    return createValidationError("required_true");
};

export const validateMinLength = (value, min) => {
    if (!hasValue(value) || toTextLength(value) >= min) { return; }
    return createValidationError("too_short", { min });
};

export const validateMaxLength = (value, max) => {
    if (!hasValue(value) || toTextLength(value) <= max) { return; }
    return createValidationError("too_long", { max });
};

export const validateLength = (value, options = {}) => {
    if (!hasValue(value)) { return; }
    const length = toTextLength(value);
    const { min = 0, max = Infinity } = options;
    if (length >= min && length <= max) { return; }
    return createValidationError("invalid_length", toLengthDetails({ min, max }));
};

export const validateMatches = (value, regex, details) => {
    if (!hasValue(value) || testRegex(regex, value)) { return; }
    return createValidationError("invalid_format", details);
};

export const validateChars = (value, regex, details) => {
    if (!hasValue(value) || testRegex(regex, value)) { return; }
    return createValidationError("invalid_chars", details);
};

export const validateHasPattern = (value, regex, details) => {
    if (!hasValue(value) || testRegex(regex, value)) { return; }
    return createValidationError("missing_letter", details);
};

export const validateOneOf = (value, allowed) => {
    if (!hasValue(value) || allowed.includes(value)) { return; }
    return createValidationError("unsupported", { allowed });
};

export const validateNotFutureDate = (date, now = Date.now()) => {
    if (!date || date.getTime() <= now) { return; }
    return createValidationError("future_date");
};

export const validateSameAs = (value, other) => {
    if (value === other) { return; }
    return createValidationError("mismatch");
};

export const validateRecurring = (value) => {
    const aliasRepeatedCharsKey = value.replace(/[ ._-]+/g, "");
    if (!/(.)\1{2,}/u.test(aliasRepeatedCharsKey)) { return; }
    return createValidationError("recurring");
}

export const parseBoolean = (raw, fallback = false) => {
    if (raw === true || raw === "true" || raw === 1 || raw === "1") {
        return { value: true, isValid: true, isEmpty: false };
    }
    if (raw === false || raw === "false" || raw === 0 || raw === "0") {
        return { value: false, isValid: true, isEmpty: false };
    }
    if (!hasValue(raw)) { return { value: fallback, isValid: true, isEmpty: true }; }
    return { value: fallback, isValid: false, isEmpty: false };
};

export const validateBooleanParsed = parsed => {
    if (parsed?.isValid) { return; }
    return createValidationError("invalid_type", { expected: "boolean" });
};
